// BOORO: approval contracts. A preview shows what the agent is about to do,
// in the words of that action, and a person may correct it before allowing.
import { oc } from "@orpc/contract";
import * as z from "zod";
import { Id } from "./ids.js";

export const APPROVAL_PREVIEW_KINDS = ["email", "calendar", "payment", "page", "generic"] as const;
export type ApprovalPreviewKind = (typeof APPROVAL_PREVIEW_KINDS)[number];

export const ApprovalPreviewSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("email"),
    to: z.string(),
    cc: z.string().nullable(),
    subject: z.string(),
    body: z.string(),
  }),
  z.object({
    kind: z.literal("calendar"),
    title: z.string(),
    start: z.string(),
    end: z.string(),
    attendees: z.array(z.string()),
  }),
  z.object({
    kind: z.literal("payment"),
    amount: z.string(),
    currency: z.string(),
    recipient: z.string(),
  }),
  z.object({
    kind: z.literal("page"),
    title: z.string(),
    content: z.string(),
    /** The page it changes; null for a new page. */
    path: z.string().nullable(),
    office: z.string().nullable(),
  }),
  z.object({
    kind: z.literal("generic"),
    fields: z.array(z.object({ label: z.string(), value: z.string() })),
  }),
]);
export type ApprovalPreview = z.infer<typeof ApprovalPreviewSchema>;

/** The approval with its preview, the task it belongs to and the agent. */
export const ApprovalDetailSchema = z.object({
  effectId: Id,
  toolName: z.string(),
  preview: ApprovalPreviewSchema,
  /** The task the run works on, so the person sees what it is for. */
  task: z.object({ ticketId: Id, ref: z.string(), title: z.string() }).nullable(),
  botId: Id,
  botName: z.string(),
  /** Why auto review asked, when it did. */
  reviewReason: z.string().nullable(),
});
export type ApprovalDetail = z.infer<typeof ApprovalDetailSchema>;

export const ApprovalPreviewInput = z.object({ effectId: Id });
export type ApprovalPreviewInput = z.infer<typeof ApprovalPreviewInput>;

/** The fields a person may change before allowing. */
export const ApprovalChangesInput = z.object({
  effectId: Id,
  changes: z.record(z.string(), z.string()),
});
export type ApprovalChangesInput = z.infer<typeof ApprovalChangesInput>;

/** An agent rule: this action is allowed for this agent from now on. */
export const AgentApprovalRuleSchema = z.object({
  id: Id,
  botId: Id,
  botName: z.string(),
  effect: z.enum(["require_approval", "always_allow"]),
  matchKind: z.enum(["tool", "connector", "category"]),
  matchValue: z.string(),
  createdAt: z.string(),
});
export type AgentApprovalRule = z.infer<typeof AgentApprovalRuleSchema>;

export const booroApprovalsContract = {
  detail: oc.input(ApprovalPreviewInput).output(ApprovalDetailSchema),
  /** Allows the action with the changed values. */
  approveWithChanges: oc.input(ApprovalChangesInput).output(z.object({ ok: z.literal(true) })),
  /** Allows the action for this agent from now on. */
  alwaysAllowForAgent: oc.input(ApprovalPreviewInput).output(z.object({ ok: z.literal(true) })),
  listAgentRules: oc.input(z.object({ botId: Id })).output(z.array(AgentApprovalRuleSchema)),
  removeAgentRule: oc.input(z.object({ ruleId: Id })).output(z.object({ ok: z.literal(true) })),
};
